import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EventCost, EventDetailDTO, EventResultDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { conMoneda, dinero, dineroCod, errorText } from "../workshops/comun";
import useEsTelefono from "../../../../utils/esTelefono";
import { cuando } from "./cuando";

interface Props { campusId: number; evento: EventDetailDTO; soloLectura: boolean; reload: () => void; notify: (t: string, ok?: boolean) => void; }

/** Resultado: ingresos (cobrado / pendiente, por tipo), costos y bruto. Los costos se agregan aquí (no al crear el evento). */
const ResultadoTab: React.FC<Props> = ({ campusId, evento, soloLectura, reload, notify }) => {
	const http = HttpManager.getInstance();
	const [r, setR] = useState<EventResultDTO | null>(null);
	const [edit, setEdit] = useState<{ concept: string; amount: string }[] | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const movil = useEsTelefono();
	useEffect(() => {
		let vigente = true;
		http.eventsService.result(campusId, evento.id).then(x => vigente && setR(x.data)).catch(e => notify(errorText(e), false));
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, evento]);
	if (!r) return <div className="wau-caja wau-sec">{S.cargando}</div>;
	const m = (v: number) => dineroCod(r.currency, v);
	const d = (v: number) => dinero(r.currency, v);
	const bajas = evento.bajasPagadas || [];
	const cifra = (label: string, v: number, oro = false) => <div><span className="wau-lab">{label}</span><div className={"wte-cifra" + (oro ? " oro" : "")}>{m(v)}</div></div>;
	const editar = () => setEdit([...r.costs.map(c => ({ concept: c.concept, amount: String(c.amount) })), { concept: "", amount: "" }]);
	const setCosto = (i: number, k: "concept" | "amount", v: string) => setEdit(cs => (cs || []).map((x, j) => j === i ? { ...x, [k]: v } : x));
	const guardarCostos = () => {
		if (!edit) return;
		const costs: EventCost[] = edit.filter(c => c.concept.trim() && c.amount.trim() !== "" && !isNaN(Number(c.amount))).map(c => ({ concept: c.concept.trim(), amount: Number(c.amount) }));
		setBusy(true);
		http.eventsService.update(campusId, { id: evento.id, costs })
			.then(() => { setEdit(null); notify(S.costosOk); reload(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const cuenta = (label: string, v: number) => <div><span className="wau-lab">{label}</span><div className="wte-cifra">{v}</div></div>;
	return (
		<div>
			<div className="wau-caja wte-grid">
				{cifra(S.ingresos, r.income.total)}
				{cifra(S.cobrado, r.income.collected)}
				{cifra(S.pendiente, r.income.pending, r.income.pending > 0)}
				{cifra(S.costosTotal, r.costsTotal)}
				{cifra(S.bruto, r.gross, r.gross < 0)}
				{cifra(S.brutoCobrado, r.grossCollected, r.grossCollected < 0)}
			</div>
			{/* R2: mesas ocupadas y togas/birretes (= graduadas y participantes vigentes) */}
			<div className="wau-caja wte-grid" style={{ marginTop: 10 }}>
				{cuenta(S.asistentesVigentes, r.attendees ?? evento.counts.total)}
				{cuenta(S.mesas, r.tables ?? 0)}
				{cuenta(S.togas, r.graduates ?? evento.counts.graduates)}
			</div>
			{/* Fase 2: quién se dio de baja después de pagar y a dónde fue lo que sobró */}
			{bajas.length ? (
				<div className="wau-caja wau-tabla">
					<h3>{S.bajasPagadas} <span className="wau-sec wau-num">({bajas.length})</span></h3>
					{movil ? bajas.map(b => (
						<div key={b.id} className="wau-tarjeta">
							<b>{b.name}</b>
							<div className="wau-num" style={{ marginTop: 4 }}>{S.bajaPago} {d(b.paid)} · {S.colSobro} {d(b.surplus)}</div>
							<div style={{ marginTop: 4 }}>{b.destinoLabel || "—"}</div>
							<div className="linea">{[b.by, cuando(b.at)].filter(Boolean).join(" · ") || "—"}</div>
							{b.note ? <div className="linea" style={{ marginTop: 4 }}>{b.note}</div> : null}
						</div>
					)) : (
						<table>
							<thead><tr><th>{S.colNombre}</th><th className="der">{S.bajaPago}</th><th className="der">{S.colSobro}</th><th>{S.colDestino}</th><th>{S.colQuien}</th><th>{S.colCuando}</th><th>{S.colNota}</th></tr></thead>
							<tbody>{bajas.map(b => (
								<tr key={b.id}>
									<td><b>{b.name}</b></td>
									<td className="der wau-num">{d(b.paid)}</td>
									<td className="der wau-num">{d(b.surplus)}</td>
									<td>{b.destinoLabel || "—"}</td>
									<td>{b.by || "—"}</td>
									<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{cuando(b.at) || "—"}</td>
									<td style={{ fontSize: 13.5 }}>{b.note || "—"}</td>
								</tr>
							))}</tbody>
						</table>
					)}
				</div>
			) : null}
			<div className="wau-dos">
				<div className="wau-caja wau-tabla">
					<h3>{S.porTipo}</h3>
					<table>
						<thead><tr><th>{S.colTipo}</th><th className="der">{S.cantidad}</th><th className="der">{S.ingresos}</th><th className="der">{S.cobrado}</th></tr></thead>
						<tbody>{r.income.byKind.map(k => <tr key={k.kind}><td>{k.label}</td><td className="der wau-num">{k.count}</td><td className="der wau-num">{m(k.income)}</td><td className="der wau-num">{m(k.collected)}</td></tr>)}</tbody>
					</table>
				</div>
				<div className="wau-caja wau-tabla">
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
						<h3>{S.costos}</h3>
						{soloLectura || edit ? null : <button type="button" className="wau-btn s mini" onClick={editar}>{S.agregarCosto}</button>}
					</div>
					{edit ? (
						<div>
							{edit.map((c, i) => (
								<div key={i} className="wte-costo">
									<input className="wau-entrada" aria-label={S.concepto} placeholder={S.concepto} value={c.concept} autoFocus={i === edit.length - 1} onChange={e => setCosto(i, "concept", e.target.value)} />
									<input type="number" min={0} step="0.01" className="wau-entrada wau-num" aria-label={conMoneda(S.monto, r.currency)} placeholder={conMoneda(S.monto, r.currency)} value={c.amount} onChange={e => setCosto(i, "amount", e.target.value)} />
									<button type="button" className="wau-btn s mini" aria-label={S.quitar} onClick={() => setEdit(cs => (cs || []).filter((_x, j) => j !== i))}>×</button>
								</div>
							))}
							<button type="button" className="wau-btn s mini" onClick={() => setEdit(cs => [...(cs || []), { concept: "", amount: "" }])}>{S.agregarCosto}</button>
							<div className="wau-acciones">
								<button type="button" className="wau-btn s" onClick={() => setEdit(null)} disabled={busy}>{S.volver}</button>
								<button type="button" className="wau-btn p" onClick={guardarCostos} disabled={busy}>{S.guardar}</button>
							</div>
						</div>
					) : (
						<table>
							<tbody>
								{r.costs.length === 0 ? <tr><td className="wau-sec">—</td></tr> : r.costs.map((c, i) => <tr key={i}><td>{c.concept}</td><td className="der wau-num">{m(c.amount)}</td></tr>)}
								<tr><td><b>{S.costosTotal}</b></td><td className="der wau-num"><b>{m(r.costsTotal)}</b></td></tr>
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
};

export default ResultadoTab;
